import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../api/client';
import { SYSTEM_BILINGUAL } from '../shared/constants/bilingualTitles';

interface TransitionLog {
  timestamp_utc?: string;
  symbol?: string;
  from_state?: string;
  to_state?: string;
  active_provider?: string;
  previous_provider?: string;
  reason?: string;
  error?: string;
  last_error?: string;
  event_type?: string;
  attempt_count?: number;
  elapsed_ms?: number;
  [key: string]: unknown;
}

interface FeedCompletenessItem {
  missing_candle_count?: number;
  completeness_pct?: string;
  [key: string]: unknown;
}

const SYMBOLS_ORDER = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'OKBUSDT', 'BGBUSDT', 'HYPEUSDT'];

const VENUE_INFO: Record<string, { venue: string; class: string; role: string }> = {
  BTCUSDT: { venue: 'BINANCE', class: 'venue-binance', role: '主流资产' },
  ETHUSDT: { venue: 'BINANCE', class: 'venue-binance', role: '主流资产' },
  SOLUSDT: { venue: 'BINANCE', class: 'venue-binance', role: '主流资产' },
  BNBUSDT: { venue: 'BINANCE', class: 'venue-binance', role: '主流资产' },
  OKBUSDT: { venue: 'OKX', class: 'venue-okx', role: '主场平台币' },
  BGBUSDT: { venue: 'BITGET', class: 'venue-bitget', role: '主场平台币' },
  HYPEUSDT: { venue: 'HYPERLIQUID', class: 'venue-hyperliquid', role: 'DEX 旗舰' },
};

function formatUTC8(dateInput?: string | Date | null): string {
  if (!dateInput) return '--';
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);
  const utc8 = new Date(d.getTime() + 8 * 3600 * 1000);
  const y = utc8.getUTCFullYear();
  const m = String(utc8.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utc8.getUTCDate()).padStart(2, '0');
  const h = String(utc8.getUTCHours()).padStart(2, '0');
  const min = String(utc8.getUTCMinutes()).padStart(2, '0');
  const sec = String(utc8.getUTCSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${sec} (UTC+8)`;
}

export const DrAuditLogView: React.FC = () => {
  const [telemetry, setTelemetry] = useState<Record<string, unknown> | null>(null);
  const [statusData, setStatusData] = useState<Record<string, unknown> | null>(null);

  const [logsClearedAt, setLogsClearedAt] = useState<string | null>(() => {
    return localStorage.getItem('market_hub_logs_cleared_at') || null;
  });
  const [showArchivedLogs, setShowArchivedLogs] = useState<boolean>(false);
  const [activeLogFilter, setActiveLogFilter] = useState<'ALL' | 'ERRORS' | 'FAILOVER'>('ALL');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLogPaused, setIsLogPaused] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 2600);
  }, []);

  const fallbackCopy = useCallback((text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
    } catch (e) {
      console.error('Fallback copy failed', e);
    }
    textArea.remove();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [tele, stat] = await Promise.all([
        apiClient.getTelemetry(),
        apiClient.getStatus(),
      ]);
      if (!isLogPaused) {
        setTelemetry(tele);
      }
      setStatusData(stat as Record<string, unknown>);
    } catch (err) {
      console.warn('Failed to load audit telemetry/status:', err);
    }
  }, [isLogPaused]);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, 4000);
    return () => clearInterval(timer);
  }, [fetchData]);

  // Extract raw audit / transition logs
  const rawLogs: TransitionLog[] = useMemo(() => {
    if (!telemetry) return [];
    return (telemetry.transition_logs as TransitionLog[]) || [];
  }, [telemetry]);

  // Partition logs into active vs archived based on logsClearedAt
  const { active, archived } = useMemo(() => {
    if (!logsClearedAt) {
      return { active: rawLogs, archived: [] };
    }
    const clearedTime = new Date(logsClearedAt).getTime();
    const act: TransitionLog[] = [];
    const arc: TransitionLog[] = [];
    for (const l of rawLogs) {
      const logTime = l.timestamp_utc ? new Date(l.timestamp_utc).getTime() : Date.now();
      if (logTime > clearedTime) {
        act.push(l);
      } else {
        arc.push(l);
      }
    }
    return { active: act, archived: arc };
  }, [rawLogs, logsClearedAt]);

  const displayPool = showArchivedLogs ? archived : active;

  // Filter logs by tab
  const filteredLogs = useMemo(() => {
    if (activeLogFilter === 'ERRORS') {
      return displayPool.filter(
        (l) => l.error || l.last_error || (l.to_state && l.to_state.includes('UNAVAILABLE'))
      );
    }
    if (activeLogFilter === 'FAILOVER') {
      return displayPool.filter(
        (l) => l.from_state !== l.to_state || (l.event_type && l.event_type.includes('FAILOVER'))
      );
    }
    return displayPool;
  }, [displayPool, activeLogFilter]);

  // Handlers
  const handleClearAuditLogs = () => {
    const nowIso = new Date().toISOString();
    setLogsClearedAt(nowIso);
    setShowArchivedLogs(false);
    localStorage.setItem('market_hub_logs_cleared_at', nowIso);
    if (statusData?.error_count != null) {
      localStorage.setItem('market_hub_error_count_cleared', String(statusData.error_count));
    }
    showToast('✅ 历史日志已标为已读并清空！已消除当前报警标记。');
  };

  const handleResetClearedState = () => {
    setLogsClearedAt(null);
    setShowArchivedLogs(false);
    localStorage.removeItem('market_hub_logs_cleared_at');
    localStorage.removeItem('market_hub_error_count_cleared');
    showToast('🔄 已重置已读标记，恢复显示全部原始日志。');
  };

  const handleToggleArchived = () => {
    setShowArchivedLogs((prev) => !prev);
  };

  const handleCopyAll = (logsToCopy: TransitionLog[]) => {
    if (!logsToCopy || logsToCopy.length === 0) {
      showToast('暂无异常或审计记录 (Clean)');
      return;
    }
    const payload = JSON.stringify(logsToCopy, null, 2);
    const label = showArchivedLogs
      ? `全部 ${logsToCopy.length} 条已读历史日志`
      : logsToCopy.length > 0
      ? `全部 ${logsToCopy.length} 条未读报错信息`
      : `全部 ${logsToCopy.length} 条归档审计日志`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(payload)
        .then(() => showToast(`✅ 已复制${label}到剪贴板！`))
        .catch(() => {
          fallbackCopy(payload);
          showToast(`✅ 已复制${label}到剪贴板！`);
        });
    } else {
      fallbackCopy(payload);
      showToast(`✅ 已复制${label}到剪贴板！`);
    }
  };

  const handleCopySingle = (l: TransitionLog, idx: number) => {
    const payload = JSON.stringify(l, null, 2);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(payload)
        .then(() => showToast(`✅ 已复制第 ${idx + 1} 条日志记录！`))
        .catch(() => {
          fallbackCopy(payload);
          showToast(`✅ 已复制第 ${idx + 1} 条日志记录！`);
        });
    } else {
      fallbackCopy(payload);
      showToast(`✅ 已复制第 ${idx + 1} 条日志记录！`);
    }
  };

  // Metrics resolution
  const rateLimit = (telemetry?.rate_limit_metrics as Record<string, number>) || {
    consumed_weight_per_minute: 15,
    remaining_quota: 1185,
  };
  const consumed = rateLimit.consumed_weight_per_minute || 15;
  const remaining = rateLimit.remaining_quota || 1185;
  const totalRate = consumed + remaining;
  const remPct = totalRate > 0 ? ((remaining / totalRate) * 100).toFixed(1) : '98.8';

  const failoverTotal = (telemetry?.failover_events_total as number) ?? 0;
  const activeIncidentStatus = (telemetry?.active_incident_status as string) || 'HEALTHY';

  let globalBadgeText = 'FSM 全链路健康';
  let globalBadgeClass = 'kpi-badge badge-green';
  if (activeIncidentStatus === 'DEGRADED') {
    globalBadgeText = '🟡 链路降级重试中';
    globalBadgeClass = 'kpi-badge badge-amber';
  } else if (activeIncidentStatus !== 'HEALTHY' || failoverTotal > 0) {
    globalBadgeText = '🔴 触发容灾熔断';
    globalBadgeClass = 'kpi-badge badge-rose';
  }

  const activeErrCount = active.length;
  const archivedCount = archived.length;

  const cachedCandles =
    (telemetry?.cached_candles_total as number) ??
    (statusData?.cached_candle_count as number) ??
    24336;
  const auditRecords =
    (telemetry?.audit_records_count as number) ??
    (statusData?.audit_record_count as number) ??
    0;

  // Completeness data
  const compData = (telemetry?.feed_completeness as Record<string, FeedCompletenessItem>) || {};
  const fsmData = (telemetry?.fsm_states as Record<string, string>) || {};
  const activeProvMap = (telemetry?.active_providers as Record<string, string>) || {};

  // Render Log Cards helper
  const renderLogCards = (logs: TransitionLog[]) => {
    if (!showArchivedLogs && active.length === 0) {
      if (archived.length > 0) {
        return (
          <div
            style={{
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              borderRadius: '8px',
              padding: '28px 20px',
              textAlign: 'center',
              color: 'var(--color-emerald)',
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
              🟢 日志已确认并标为已读 · 当前无新增异常
            </div>
            <div
              style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                maxWidth: '580px',
                margin: '0 auto',
                lineHeight: 1.6,
              }}
            >
              所有历史异常变迁事件（共 {archived.length} 条）已于{' '}
              <strong>{formatUTC8(logsClearedAt)}</strong>{' '}
              归档确认。当前海外东京节点守护进程稳定运行，各数据源全链路 HEALTHY，零未读告警。
            </div>
            <div
              style={{
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={handleToggleArchived}
                className="btn-copy-main"
                style={{
                  background: 'rgba(56, 189, 248, 0.15)',
                  color: '#38bdf8',
                  borderColor: 'rgba(56, 189, 248, 0.4)',
                }}
              >
                📜 查看已读历史记录 ({archived.length} 条)
              </button>
              <button
                onClick={handleResetClearedState}
                className="btn-copy-main"
                style={{
                  background: 'rgba(148, 163, 184, 0.1)',
                  color: 'var(--text-secondary)',
                  borderColor: 'rgba(148, 163, 184, 0.3)',
                }}
              >
                🔄 重置已读标记
              </button>
            </div>
          </div>
        );
      } else {
        return (
          <div
            style={{
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              borderRadius: '8px',
              padding: '28px 20px',
              textAlign: 'center',
              color: 'var(--color-emerald)',
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
              🟢 零异常 · 状态良好
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {activeLogFilter === 'ALL'
                ? '守护进程持续稳定运行，各数据源心跳正常，未捕获到任何容灾熔断或异常事件。'
                : '当前筛选分类下暂无异常事件记录。'}
            </div>
            <div
              style={{
                fontSize: '10px',
                color: 'var(--text-muted)',
                fontFamily: 'var(--font-mono)',
                marginTop: '8px',
              }}
            >
              Overseas VPS Tokyo · Ingestion Pipeline Clean
            </div>
          </div>
        );
      }
    }

    if (showArchivedLogs && logs.length === 0) {
      return (
        <div
          style={{
            background: 'rgba(15, 23, 42, 0.6)',
            border: '1px dashed rgba(51, 65, 85, 0.6)',
            borderRadius: '8px',
            padding: '24px 20px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}
        >
          <div>当前筛选分类下无历史归档记录。</div>
          <button
            onClick={handleToggleArchived}
            className="btn-copy-main"
            style={{ margin: '12px auto 0' }}
          >
            返回未读视图
          </button>
        </div>
      );
    }

    return (
      <>
        {showArchivedLogs && (
          <div
            style={{
              background: 'rgba(56, 189, 248, 0.08)',
              border: '1px solid rgba(56, 189, 248, 0.25)',
              borderRadius: '6px',
              padding: '8px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '11px',
              marginBottom: '4px',
            }}
          >
            <span style={{ color: '#38bdf8' }}>
              📜 当前展示已读历史归档记录（于 {formatUTC8(logsClearedAt)} 前发生）
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleToggleArchived}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-emerald)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                返回未读视图
              </button>
              <button
                onClick={handleResetClearedState}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                重置标记
              </button>
            </div>
          </div>
        )}
        {logs.map((l, idx) => {
          const isErr = Boolean(
            l.error || l.last_error || (l.to_state && l.to_state.includes('UNAVAILABLE'))
          );
          const isArchived =
            showArchivedLogs ||
            (logsClearedAt &&
              l.timestamp_utc &&
              new Date(l.timestamp_utc).getTime() <= new Date(logsClearedAt).getTime());
          const tagColor = isArchived
            ? 'var(--text-muted)'
            : isErr
            ? 'var(--color-rose)'
            : 'var(--color-amber)';
          const sym = l.symbol || 'SYSTEM';
          const vInfo = VENUE_INFO[sym] || { venue: 'MAIN', class: 'venue-binance', role: '主流' };
          const timeStr = l.timestamp_utc
            ? `${formatUTC8(l.timestamp_utc)} · ${l.timestamp_utc}`
            : '刚刚';
          const errorMsg = l.last_error || l.error || l.reason || JSON.stringify(l);

          return (
            <div
              key={idx}
              className="log-entry-card"
              style={{
                borderLeft: `3px solid ${tagColor}`,
                opacity: isArchived ? 0.82 : 1,
              }}
            >
              <div className="log-card-header">
                <div className="log-card-tags">
                  <span className={`venue-pill ${vInfo.class}`}>{sym}</span>
                  <span style={{ color: tagColor, fontWeight: 700 }}>
                    [{l.event_type || (isErr ? 'ERROR' : 'FAILOVER')}]
                  </span>
                  {isArchived && (
                    <span
                      className="kpi-badge"
                      style={{
                        background: 'rgba(148, 163, 184, 0.15)',
                        color: 'var(--text-muted)',
                        fontSize: '9px',
                        padding: '2px 6px',
                      }}
                    >
                      已读归档
                    </span>
                  )}
                  <span>{timeStr}</span>
                </div>
                <button className="btn-copy-single" onClick={() => handleCopySingle(l, idx)}>
                  📋 复制单条
                </button>
              </div>

              {(l.from_state || l.to_state || l.active_provider) && (
                <div style={{ fontSize: '11px', marginTop: '2px' }}>
                  {(l.from_state || l.to_state) && (
                    <>
                      <span style={{ color: 'var(--text-muted)' }}>状态变迁: </span>
                      <strong style={{ color: 'var(--color-amber)' }}>
                        {l.from_state || 'UNKNOWN'}
                      </strong>{' '}
                      ➔{' '}
                      <strong
                        style={{
                          color: isErr
                            ? isArchived
                              ? 'var(--text-secondary)'
                              : 'var(--color-rose)'
                            : 'var(--color-emerald)',
                        }}
                      >
                        {l.to_state || 'HEALTHY'}
                      </strong>
                    </>
                  )}
                  {l.active_provider && (
                    <>
                      <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>
                        主数据源:{' '}
                      </span>
                      <strong>{l.active_provider}</strong>
                    </>
                  )}
                </div>
              )}

              <div
                className="log-card-body"
                style={{
                  marginTop: '4px',
                  color: isArchived
                    ? 'var(--text-secondary)'
                    : isErr
                    ? 'var(--color-rose)'
                    : 'var(--color-amber)',
                }}
              >
                {errorMsg}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="view-panel" id="view-dr-audit" role="tabpanel" aria-labelledby="nav-dr">
      {/* View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <h2 className="view-main-title bilingual-header-title-zh">{SYSTEM_BILINGUAL.audit.icon} {SYSTEM_BILINGUAL.audit.zh}</h2>
          </div>
          <span className="bilingual-header-subtitle-en" title="DR AUDIT & RECONCILIATION LOG">{SYSTEM_BILINGUAL.audit.en}</span>
          <p className="view-subtitle">
            全链路状态机健康监测 · 数据流完整性缺棒率 (Zero Missing) · 实时审计与历史归档排查
          </p>
        </div>
      </div>

      {/* FSM Disaster Recovery & Event Audit Bar */}
      <section className="fsm-audit-strip" id="fsm-audit-strip">
        <div className="fsm-strip-left">
          <div className="fsm-strip-title">
            <span>🚨 全域容灾审计与系统日志中枢</span>
          </div>
          <span className={globalBadgeClass} id="fsm-global-badge">
            {globalBadgeText}
          </span>
        </div>

        <div className="fsm-strip-metrics">
          <div className="fsm-metric-pill">
            <span>API 权重: </span>
            <strong id="rate-consumed-val">
              {consumed} / {totalRate}
            </strong>
            <span style={{ color: 'var(--color-emerald)', fontSize: '10px', marginLeft: '2px' }}>
              ({remPct}% 余量)
            </span>
          </div>
          <div className="fsm-metric-pill">
            <span>容灾熔断: </span>
            <strong
              id="failover-count-val"
              style={{
                color: failoverTotal === 0 ? 'var(--color-emerald)' : 'var(--color-rose)',
              }}
            >
              {failoverTotal} 次熔断
            </strong>
            <span
              id="failover-subtext"
              style={{
                color: failoverTotal === 0 ? 'var(--text-muted)' : 'var(--color-amber)',
                fontSize: '10px',
                marginLeft: '2px',
              }}
            >
              {failoverTotal === 0 ? '(未发生主动切换)' : `累计触发 ${failoverTotal} 次容灾切换`}
            </span>
          </div>
          <div className="fsm-metric-pill">
            <span>异常审计: </span>
            <strong
              id="fsm-strip-err-count"
              style={{
                color: activeErrCount === 0 ? 'var(--color-emerald)' : 'var(--color-rose)',
              }}
            >
              {activeErrCount === 0
                ? archivedCount > 0
                  ? `0 异常 (已读 ${archivedCount})`
                  : '0 异常'
                : `${activeErrCount} 处异常`}
            </strong>
          </div>
          <div className="fsm-metric-pill" style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            <span>
              烛线缓存:{' '}
              <strong id="cached-candles-count" style={{ color: 'var(--text-primary)' }}>
                {cachedCandles} 根
              </strong>{' '}
              · 审计数:{' '}
              <strong id="audit-records-count" style={{ color: 'var(--text-primary)' }}>
                {auditRecords}
              </strong>
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="btn-open-audit-modal"
            id="btn-open-audit-modal"
            onClick={() => setIsModalOpen(true)}
          >
            <span>📋 弹窗全屏深度排查</span>
            {activeErrCount > 0 && (
              <span className="audit-badge-pill" id="audit-modal-badge">
                {activeErrCount}
              </span>
            )}
          </button>
          {activeErrCount > 0 && (
            <button
              className="btn-clear-quick"
              id="btn-clear-quick"
              onClick={handleClearAuditLogs}
              title="标为已读 / 清空当前错误告警"
            >
              <span>🧹 标为已读</span>
            </button>
          )}
        </div>
      </section>

      {/* Full-Width Data Integrity & Causal Audit Panel */}
      <section className="panel-card integrity-panel" id="integrity-panel">
        <div className="panel-title-bar">
          <div className="panel-title">
            <span>📈 资产数据流完整性与因果律审计</span>
          </div>
          <span
            style={{
              fontSize: '11px',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            因果滞后窗口 W ≥ 3s (Causal Closed Bar) · 严格闭合判定
          </span>
        </div>

        {/* Feed Completeness Table (Full Width) */}
        <div className="table-responsive">
          <table className="styled-table">
            <thead>
              <tr>
                <th>资产标的</th>
                <th>主场数据源</th>
                <th>FSM 容灾状态</th>
                <th>缺棒数</th>
                <th>完整率</th>
                <th>健康评级</th>
              </tr>
            </thead>
            <tbody id="completeness-tbody">
              {SYMBOLS_ORDER.map((sym) => {
                const c = compData[sym] || { missing_candle_count: 0, completeness_pct: '100.00%' };
                const prov = activeProvMap[sym] || (VENUE_INFO[sym]?.venue || 'BINANCE');
                const fsm = fsmData[sym] || 'HEALTHY';
                const missingCount = c.missing_candle_count || 0;
                const isPerfect = missingCount === 0;
                const isHealthy = fsm === 'HEALTHY' || fsm === 'HEALTHY_PRIMARY';

                return (
                  <tr key={sym}>
                    <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{sym}</td>
                    <td>
                      <span className={`venue-pill ${VENUE_INFO[sym]?.class || 'venue-binance'}`}>
                        {prov}
                      </span>
                    </td>
                    <td>
                      {isHealthy ? (
                        <span style={{ color: 'var(--color-emerald)', fontWeight: 600 }}>HEALTHY</span>
                      ) : (
                        <span style={{ color: 'var(--color-amber)' }}>{fsm}</span>
                      )}
                    </td>
                    <td style={{ color: isPerfect ? 'var(--color-emerald)' : 'var(--color-rose)' }}>
                      {missingCount}
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      {c.completeness_pct || '100.00%'}
                    </td>
                    <td>
                      {isPerfect ? (
                        <span className="kpi-badge badge-green">🟢 完好</span>
                      ) : (
                        <span className="kpi-badge badge-rose">🔴 缺棒</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Embedded Audit Log Explorer */}
      <section className="panel-card embedded-audit-section" id="embedded-audit-section">
        <div className="panel-title-bar">
          <div className="panel-title bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="bilingual-card-title-zh">📋 容灾审计与异常事件流水</span>
              <span className="audit-badge-pill" id="embedded-audit-count">
                {filteredLogs.length}
              </span>
            </div>
            <span className="bilingual-card-subtitle-en">LIVE DR AUDIT STREAM</span>
          </div>
          <div className="audit-toolbar-actions">
            <div className="audit-filter-pills">
              <button
                className={`filter-pill ${activeLogFilter === 'ALL' ? 'active' : ''}`}
                id="emb-filter-all"
                onClick={() => setActiveLogFilter('ALL')}
              >
                全部 ALL
              </button>
              <button
                className={`filter-pill ${activeLogFilter === 'ERRORS' ? 'active' : ''}`}
                id="emb-filter-errors"
                onClick={() => setActiveLogFilter('ERRORS')}
              >
                异常/熔断
              </button>
              <button
                className={`filter-pill ${activeLogFilter === 'FAILOVER' ? 'active' : ''}`}
                id="emb-filter-failover"
                onClick={() => setActiveLogFilter('FAILOVER')}
              >
                状态切换
              </button>
            </div>
            <button
              className="btn-copy-main"
              onClick={() => handleCopyAll(filteredLogs)}
              title="一键复制当前显示的所有日志 (JSON 格式)"
            >
              <span>📋 复制当前日志</span>
            </button>
            <button
              className="btn-clear-quick"
              id="embedded-btn-clear"
              onClick={handleClearAuditLogs}
              title="标为已读 / 清空当前错误告警"
            >
              <span>🧹 标为已读</span>
            </button>
            {archivedCount > 0 && (
              <button
                className="btn-copy-main"
                id="embedded-btn-toggle-archived"
                onClick={handleToggleArchived}
              >
                <span id="embedded-archived-toggle-text">
                  {showArchivedLogs
                    ? `👀 仅看未读 (${active.length})`
                    : `📜 查看历史已读 (${archived.length})`}
                </span>
              </button>
            )}
            <button
              className="btn-copy-main"
              onClick={handleResetClearedState}
              title="重置本地已读标记，恢复全部日志"
            >
              <span>🔄 重置已读标记</span>
            </button>
          </div>
        </div>
        <div className="embedded-audit-body" id="embedded-audit-log-container">
          {renderLogCards(filteredLogs)}
        </div>
      </section>

      {/* Modal Dialog for Full-Screen Audit */}
      {isModalOpen && (
        <div
          className="modal-backdrop"
          id="audit-modal"
          style={{ display: 'flex' }}
          onClick={() => setIsModalOpen(false)}
        >
          <div className="modal-audit-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <div className="modal-head-titles">
                <div className="modal-h1">
                  <span>🛡️ 实时容灾状态机与异常审计日志</span>
                  <span className={globalBadgeClass} id="modal-fsm-status-badge">
                    {globalBadgeText}
                  </span>
                </div>
                <div className="modal-sub">
                  海外东京节点 (Overseas Edge JP-01) · 真实状态机事件流 · 支持一键复制反馈错误信息
                </div>
              </div>
              <button
                className="btn-modal-close"
                id="btn-close-audit-modal"
                title="关闭 (ESC)"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            {/* Controls Toolbar */}
            <div className="audit-ctrl-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div className="filter-tabs">
                  <button
                    className={`btn-tab ${activeLogFilter === 'ALL' ? 'active' : ''}`}
                    id="filter-tab-all"
                    onClick={() => setActiveLogFilter('ALL')}
                  >
                    全部事件
                  </button>
                  <button
                    className={`btn-tab ${activeLogFilter === 'ERRORS' ? 'active' : ''}`}
                    id="filter-tab-errors"
                    onClick={() => setActiveLogFilter('ERRORS')}
                  >
                    仅看报错/异常
                  </button>
                  <button
                    className={`btn-tab ${activeLogFilter === 'FAILOVER' ? 'active' : ''}`}
                    id="filter-tab-failover"
                    onClick={() => setActiveLogFilter('FAILOVER')}
                  >
                    容灾切换
                  </button>
                </div>
                <button
                  className="btn-tab"
                  id="btn-toggle-log-pause"
                  onClick={() => {
                    setIsLogPaused((prev) => {
                      const next = !prev;
                      showToast(next ? '已暂停日志流刷新，便于阅读' : '已恢复实时日志流同步');
                      return next;
                    });
                  }}
                  style={{ border: '1px solid rgba(51, 65, 85, 0.6)', padding: '4px 8px' }}
                >
                  <span id="log-pause-text">
                    {isLogPaused ? '▶️ 恢复刷新' : '⏸️ 暂停刷新'}
                  </span>
                </button>
                {archivedCount > 0 && (
                  <button
                    className={`btn-history-toggle ${showArchivedLogs ? 'active' : ''}`}
                    id="btn-toggle-archived"
                    onClick={handleToggleArchived}
                  >
                    <span id="archived-toggle-text">
                      {showArchivedLogs
                        ? `👀 仅看未读 (${active.length})`
                        : `📜 查看历史已读 (${archived.length})`}
                    </span>
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn-clear-logs"
                  id="btn-clear-audit-logs"
                  onClick={handleClearAuditLogs}
                >
                  <span>🧹 标为已读 / 清空</span>
                </button>
                <button
                  className="btn-copy-main"
                  id="btn-copy-all-errors"
                  onClick={() => handleCopyAll(filteredLogs)}
                >
                  <span>📋 一键复制报错信息</span>
                </button>
              </div>
            </div>

            {/* Log Stream Body in Modal */}
            <div className="audit-stream-container" id="modal-audit-log-container">
              {renderLogCards(filteredLogs)}
            </div>
          </div>
        </div>
      )}

      {/* Toast Feedback */}
      {toastMessage && (
        <div className="toast-popup" id="copy-toast" style={{ display: 'flex' }}>
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
